import React from 'react'
import { Box, Container, Typography } from '@mui/material'
import { useLocale } from 'next-intl'

import highlightsData from '@/src/constants/homeHighlights.json'

type Highlight = {
    Service: string
    Description_English: string
    Title_Arabic: string
    Description_Arabic: string
    Availability: string
}

type HighlightsData = {
    sectionTitle: {
        en: string
        ar: string
    }
    items: Highlight[]
}

interface HomeHighlightsProps {
    locale: string
    maxWidth?: 'lg' | 'xl' | false
    isMobile?: boolean
    domain?: string
}

const HomeHighlights: React.FC<HomeHighlightsProps> = ({locale, maxWidth = 'xl', isMobile = false, domain = 'ALL' }) => {
    const isArabic = locale?.startsWith('ar')
    const { sectionTitle, items } = highlightsData as HighlightsData

    const domainUpper = (domain || 'ALL').toUpperCase()

    return (
        <Box sx={{ bgcolor: 'grey.50', py: { xs: 3, md: 4 } }}>
            <Container maxWidth={maxWidth} sx={{ px: { xs: 2, sm: 3 } }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        mb: { xs: 3, md: 3 },
                        color: 'text.primary',
                        fontSize: { xs: '1.25rem', md: '1.5rem' },
                        textAlign: 'left'
                    }}
                >
                    {isArabic ? sectionTitle.ar : sectionTitle.en}
                </Typography>

                <Box component="ul" sx={{ listStyle: 'disc', pl: { xs: 3, md: 4 }, pr: { xs: 1, md: 0 }, m: 0 }}>
                    {items
                        .filter(item => {
                            if (!item.Availability) return true
                            if (domainUpper === 'ALL') return true
                            const allowed = item.Availability.split('&').map(code => code.trim().toUpperCase())
                            return allowed.includes(domainUpper)
                        })
                        .map(item => {
                            const title = isArabic && item.Title_Arabic ? item.Title_Arabic : item.Service
                            const description =
                                isArabic && item.Description_Arabic ? item.Description_Arabic : item.Description_English

                            return (
                                <Box
                                    key={item.Service}
                                    component="li"
                                    sx={{
                                        mb: 2.5,
                                        color: 'text.primary',
                                        '&:last-of-type': { mb: 0 }
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.4 }}
                                    >
                                        {title}
                                    </Typography>
                                    {description ? (
                                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                            {description}
                                        </Typography>
                                    ) : null}
                                </Box>
                            )
                        })}
                </Box>
            </Container>
        </Box>
    )
}

export default HomeHighlights
